import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { tcRunApi } from '@/api/tcRunApi';
import { calibrationMatrixKeys } from '@/queries/useCalibrationMatrix';
import type {
  UpdateTcRunRatingsRequest,
  UpdateTcRunNotesForCollatorRequest,
  UpdateTcRunConfidentialNotesRequest,
  RunningOrderItem,
} from '@/api/types/tcRun.types';
import { appraisalCycleKeys } from './useAppraisalCycles';
import { appraisalDetailKeys } from './useAppraisalDetail';

export const tcRunKeys = {
  all: ['tcRun'] as const,
  detail: (tcId: number) => ['tcRun', tcId] as const,
  appraisee: (tcId: number, appraisalId: number) => ['tcRun', tcId, 'appraisee', appraisalId] as const,
};

export function useTcRunDetail(tcId: number) {
  return useQuery({
    queryKey: tcRunKeys.detail(tcId),
    queryFn: () => tcRunApi.getTcRunDetail(tcId),
    enabled: !!tcId,
    refetchOnWindowFocus: 'always',
    staleTime: 0,
  });
}

export function useTcRunAppraiseeDetail(tcId: number, appraisalId: number) {
  return useQuery({
    queryKey: tcRunKeys.appraisee(tcId, appraisalId),
    queryFn: () => tcRunApi.getTcRunAppraiseeDetail(tcId, appraisalId),
    enabled: !!tcId && !!appraisalId,
    refetchOnWindowFocus: 'always',
    staleTime: 0,
  });
}

export function useUpdateTcRunRatings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tcId,
      appraisalId,
      data,
      rowVersion,
    }: {
      tcId: number;
      appraisalId: number;
      data: Omit<UpdateTcRunRatingsRequest, 'rowVersion'>;
      rowVersion?: string;
    }) => tcRunApi.updateTcRunRatings(tcId, appraisalId, { ...data, rowVersion }),
    onSuccess: (_, { tcId, appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.detail(tcId),
      });
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.appraisee(tcId, appraisalId),
      });
      queryClient.invalidateQueries({
        queryKey: calibrationMatrixKeys.all,
      });
    },
    onError: (error, { tcId, appraisalId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.detail(tcId),
        });
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.appraisee(tcId, appraisalId),
        });
      }
    },
  });
}

export function useUpdateTcRunNotesForCollator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tcId,
      appraisalId,
      data,
      rowVersion,
    }: {
      tcId: number;
      appraisalId: number;
      data: Omit<UpdateTcRunNotesForCollatorRequest, 'rowVersion'>;
      rowVersion?: string;
    }) => tcRunApi.updateTcRunNotesForCollator(tcId, appraisalId, { ...data, rowVersion }),
    onSuccess: (_, { tcId, appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.detail(tcId),
      });
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.appraisee(tcId, appraisalId),
      });
      queryClient.invalidateQueries({
        queryKey: calibrationMatrixKeys.all,
      });
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      queryClient.invalidateQueries({
        queryKey: appraisalDetailKeys.detail(String(appraisalId)),
      });
    },
    onError: (error, { tcId, appraisalId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.detail(tcId),
        });
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.appraisee(tcId, appraisalId),
        });
        queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
        queryClient.invalidateQueries({
          queryKey: appraisalDetailKeys.detail(String(appraisalId)),
        });
      }
    },
  });
}

export function useUpdateTcRunConfidentialNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tcId,
      appraisalId,
      data,
      rowVersion,
    }: {
      tcId: number;
      appraisalId: number;
      data: Omit<UpdateTcRunConfidentialNotesRequest, 'rowVersion'>;
      rowVersion?: string;
    }) => tcRunApi.updateTcRunConfidentialNotes(tcId, appraisalId, { ...data, rowVersion }),
    onSuccess: (_, { tcId, appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.detail(tcId),
      });
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.appraisee(tcId, appraisalId),
      });
      queryClient.invalidateQueries({
        queryKey: calibrationMatrixKeys.all,
      });
    },
    onError: (error, { tcId, appraisalId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.detail(tcId),
        });
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.appraisee(tcId, appraisalId),
        });
      }
    },
  });
}

export function useApproveForDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tcId,
      appraisalId,
      rowVersion,
    }: {
      tcId: number;
      appraisalId: number;
      rowVersion?: string;
    }) => tcRunApi.approveForDelivery(tcId, appraisalId, rowVersion),
    onSuccess: (_, { tcId, appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.detail(tcId),
      });
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.appraisee(tcId, appraisalId),
      });
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
    },
    onError: (error, { tcId, appraisalId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.detail(tcId),
        });
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.appraisee(tcId, appraisalId),
        });
        queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      }
    },
  });
}

export function useReturnToCollator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tcId,
      appraisalId,
      rowVersion,
    }: {
      tcId: number;
      appraisalId: number;
      rowVersion?: string;
    }) => tcRunApi.returnToCollator(tcId, appraisalId, rowVersion),
    onSuccess: (_, { tcId, appraisalId }) => {
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.detail(tcId),
      });
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.appraisee(tcId, appraisalId),
      });
      queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
    },
    onError: (error, { tcId, appraisalId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.detail(tcId),
        });
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.appraisee(tcId, appraisalId),
        });
        queryClient.invalidateQueries({ queryKey: appraisalCycleKeys.all });
      }
    },
  });
}

export function useUpdateRunningOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tcId,
      items,
      rowVersion,
    }: {
      tcId: number;
      items: RunningOrderItem[];
      rowVersion?: string;
    }) => tcRunApi.updateRunningOrder(tcId, { items, rowVersion }),
    onSuccess: (_, { tcId }) => {
      queryClient.invalidateQueries({
        queryKey: tcRunKeys.detail(tcId),
      });
    },
    onError: (error, { tcId }) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        queryClient.invalidateQueries({
          queryKey: tcRunKeys.detail(tcId),
        });
      }
    },
  });
}
